import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarCheck, MapPin, ShieldCheck, Check, Quote, BadgeCheck, MessagesSquare, Compass } from "lucide-react";
import { SERVICES, FEATURED } from "@/data/services";
import { STATS, HOME_FAQS, bookingUrl } from "@/data/site";
import { PAGE_SEO } from "@/data/pages";
import { useSeo, faqSchema } from "@/lib/seo";
import { ServiceIcon } from "@/components/site/ServiceIcon";
import { Eyebrow, FaqList, EnquirySection, CtaBanner, ServiceCard, LeadTeaser } from "@/components/site/Blocks";
import { ReviewsSection, Stars } from "@/components/site/Reviews";
import { LatestPosts } from "@/components/site/Posts";
import { Reveal } from "@/components/site/Reveal";

const HOME_LD = { "@context": "https://schema.org", ...faqSchema(HOME_FAQS) };

const Hero = () => (
  <section className="relative overflow-hidden bg-cream" data-testid="home-hero">
    <div className="pointer-events-none absolute -left-32 -top-24 h-96 w-96 rounded-full bg-brand-blue/10 blur-3xl" aria-hidden="true" />
    <div className="pointer-events-none absolute right-[-10%] top-1/3 h-[32rem] w-[32rem] rounded-full bg-brand-red/5 blur-3xl" aria-hidden="true" />
    <div className="container-x grid items-center gap-12 pb-28 pt-12 lg:grid-cols-[1.05fr_0.95fr] lg:pb-36 lg:pt-16">
      <div className="reveal">
        <span className="trust-pill" data-testid="hero-rating-chip"><Stars n={5} className="h-3.5 w-3.5" /> Trusted by clients across Canada & abroad</span>
        <h1 className="mt-6 font-display text-4xl leading-[1.03] text-navy sm:text-5xl lg:text-7xl">Canadian immigration<br /><em className="text-brand-red">made clear.</em></h1>
        <p className="mt-6 max-w-xl text-base text-slate-600 md:text-lg">Professional guidance for people looking to visit, study, work or immigrate to Canada, with a clear understanding of the process and the options that may apply to their situation.</p>
        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <a href={bookingUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center" data-testid="hero-book-btn"><CalendarCheck className="h-4 w-4" /> Book a Consultation</a>
          <a href="#enquiry" className="btn-outline justify-center" data-testid="hero-eligibility-btn">Check Your Eligibility <ArrowRight className="h-4 w-4" /></a>
        </div>
        <div className="mt-10 flex flex-wrap gap-2.5">
          {[[MapPin, "Mississauga, Ontario"], [ShieldCheck, "RCIC-IRB"], [BadgeCheck, "Professional Guidance"]].map(([I, t]) => (
            <span key={t} className="trust-pill"><I className="h-4 w-4 text-brand-red" /> {t}</span>
          ))}
        </div>
      </div>
      <div className="relative reveal" style={{ animationDelay: "120ms" }}>
        <div className="absolute -right-4 -top-4 bottom-10 left-10 rounded-[2rem] bg-navy/90" aria-hidden="true" />
        <picture>
          <img src="/assets/hero-premium.jpg" alt="Toronto skyline with the CN Tower at dusk over Lake Ontario" width="848" height="1264" fetchPriority="high" className="relative h-[440px] w-full rounded-[1.75rem] object-cover shadow-premium-lg lg:h-[580px]" />
        </picture>
        <div className="absolute left-4 top-5 flex items-center gap-2 rounded-xl bg-white/90 px-3.5 py-2.5 shadow-xl backdrop-blur animate-float-slow" data-testid="hero-badge">
          <span className="font-display text-2xl font-semibold text-navy">5.0</span>
          <span className="leading-tight"><Stars n={5} className="h-3 w-3" /><span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-wide text-slate-500">Google rating</span></span>
        </div>
        <div className="absolute -bottom-6 left-4 right-4 flex items-center gap-4 rounded-2xl bg-white p-4 shadow-premium-lg ring-1 ring-black/5 sm:left-auto sm:right-6 sm:w-80" data-testid="hero-founder-card">
          <img src="/assets/amarvir.webp" alt="" width="56" height="56" className="h-14 w-14 rounded-full object-cover object-top ring-2 ring-brand-red/20" />
          <div>
            <p className="text-sm font-bold text-navy">Amarvir Singh</p>
            <p className="text-xs text-slate-500">Founder · Regulated Canadian Immigration Consultant (RCIC-IRB)</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Dock = () => (
  <section className="relative z-10 -mt-16" aria-label="Featured immigration services">
    <div className="container-x grid grid-cols-2 overflow-hidden rounded-2xl border border-line bg-white shadow-premium md:grid-cols-3 lg:grid-cols-6">
      {FEATURED.map((f, i) => (
        <Link key={f.slug} to={`/services/${f.slug}`} className={`group flex flex-col gap-3 border-line p-5 transition-colors hover:bg-cream ${i ? "border-l" : ""} max-lg:border-b`} data-testid={`dock-${f.slug}`}>
          <span className={`grid h-10 w-10 place-items-center rounded-full ${i === 0 ? "bg-brand-red text-white" : "bg-cream text-navy"} transition-colors group-hover:bg-brand-red group-hover:text-white`}><ServiceIcon name={f.icon} /></span>
          <strong className="text-sm leading-snug text-navy">{f.label}</strong>
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-red">Explore <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" /></span>
        </Link>
      ))}
    </div>
  </section>
);

const Counter = ({ value, suffix }) => {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    const el = ref.current;
    const obs = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      obs.disconnect();
      const start = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - start) / 1500);
        setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [value]);
  return <span ref={ref}>{n.toLocaleString()}{suffix}</span>;
};

const Stats = () => (
  <section className="py-24" data-testid="home-stats">
    <div className="container-x grid gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
      <Reveal>
        <Eyebrow>Our track record</Eyebrow>
        <h2 className="mt-4 font-display text-3xl text-navy sm:text-4xl">Trusted by clients in Canada and around the world.</h2>
      </Reveal>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 90} className="group relative overflow-hidden rounded-2xl border border-line bg-white p-6 shadow-premium transition-transform duration-300 hover:-translate-y-1" data-testid={`stat-${i}`}>
            <span className="absolute inset-x-0 top-0 h-1 bg-brand-red/80" aria-hidden="true" />
            <p className="font-display text-4xl text-navy"><Counter value={s.value} suffix={s.suffix} /></p>
            <p className="mt-2 text-sm text-slate-600">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Services = () => (
  <section className="bg-[#faf8f5] py-24" id="services" data-testid="home-services">
    <div className="container-x">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div className="max-w-2xl">
          <Eyebrow>Immigration services</Eyebrow>
          <h2 className="mt-4 font-display text-3xl text-navy sm:text-4xl">A clear path starts with the right <em className="text-brand-red">service.</em></h2>
        </div>
        <p className="max-w-md text-slate-600">Choose the area that best matches your situation. Each service page explains who it may suit, the typical documents and how the process works.</p>
      </div>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {SERVICES.map((s, i) => <Reveal key={s.slug} delay={(i % 4) * 70}><ServiceCard s={s} i={i} /></Reveal>)}
      </div>
      <p className="mt-10 text-sm text-slate-500" data-testid="services-note">No immigration outcome is guaranteed. Eligibility and requirements depend on individual circumstances and the applicable rules at the time of application.</p>
    </div>
  </section>
);

const Expiry = () => (
  <section className="navy-depth relative overflow-hidden text-white" data-testid="home-expiry">
    <div className="pointer-events-none absolute -right-16 top-0 h-80 w-80 rounded-full bg-brand-red/20 blur-3xl" aria-hidden="true" />
    <div className="container-x relative grid gap-10 py-20 lg:grid-cols-2 lg:items-center">
      <div>
        <p className="eyebrow is-light flex items-center gap-2"><span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-red opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-brand-red" /></span>Work permit expiring?</p>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl">Don't risk your status in Canada.</h2>
        <p className="mt-5 max-w-lg text-white/80">Explore your options before your work permit expires. The appropriate pathway depends on your status, eligibility and circumstances, and applying on time can matter.</p>
        <Link to="/services/work-permit" className="btn-white mt-8" data-testid="expiry-cta-btn">Check Your Options <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <ul className="grid gap-3 sm:grid-cols-2">
        {["Work-to-study options", "Extension applications", "Employer support options", "PR pathway review"].map((t, i) => (
          <Reveal as="li" key={t} delay={i * 80} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/10 p-5 font-semibold backdrop-blur"><span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-brand-red/90"><Check className="h-4 w-4" /></span> {t}</Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export const Founder = () => (
  <section className="py-24" data-testid="founder-section">
    <div className="container-x grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
      <Reveal className="relative mx-auto w-full max-w-md">
        <div className="absolute -left-4 -top-4 h-full w-full rounded-[1.75rem] border-2 border-brand-blue/30" aria-hidden="true" />
        <div className="absolute -bottom-4 -right-4 h-24 w-24 rounded-2xl bg-brand-red/10" aria-hidden="true" />
        <img src="/assets/amarvir.webp" alt="Amarvir Singh, founder of Chahal Overseas Immigration" width="720" height="900" loading="lazy" className="relative w-full rounded-[1.75rem] object-cover shadow-premium" />
      </Reveal>
      <Reveal delay={100}>
        <Eyebrow>Meet the founder</Eyebrow>
        <h2 className="mt-4 font-display text-3xl text-navy sm:text-4xl">Committed to your <em className="text-brand-red">Canadian</em> journey.</h2>
        <p className="mt-6 text-slate-600">Amarvir Singh is the founder of Chahal Overseas Immigration, providing professional guidance to individuals and families navigating Canadian immigration processes from our office in Mississauga.</p>
        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[[ShieldCheck, "Professional guidance"], [Compass, "Personalized strategy"], [MessagesSquare, "Clear communication"]].map(([I, t]) => (
            <div key={t} className="rounded-xl border border-line bg-white p-4 shadow-premium"><span className="grid h-9 w-9 place-items-center rounded-lg bg-cream text-brand-red"><I className="h-5 w-5" /></span><p className="mt-3 text-sm font-semibold text-navy">{t}</p></div>
          ))}
        </div>
        <figure className="mt-8 border-l-2 border-brand-red pl-5">
          <Quote className="h-5 w-5 text-brand-red" />
          <blockquote className="mt-2 font-display text-xl italic text-navy">Guiding people. Building futures.</blockquote>
          <figcaption className="mt-2 text-sm text-slate-500">Amarvir Singh, RCIC-IRB, Founder</figcaption>
        </figure>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <a href={bookingUrl()} target="_blank" rel="noopener noreferrer" className="btn-primary justify-center" data-testid="founder-book-btn">Speak With an Immigration Professional</a>
          <Link to="/about" className="btn-outline justify-center" data-testid="founder-about-link">About us</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function HomePage() {
  useSeo({
    ...PAGE_SEO.home,
    path: "/",
    jsonLd: HOME_LD,
  });
  return (
    <>
      <Hero />
      <Dock />
      <Stats />
      <Services />
      <Expiry />
      <LeadTeaser />
      <Founder />
      <ReviewsSection className="bg-[#faf8f5]" />
      <LatestPosts />
      <section className="bg-cream py-24" data-testid="home-faq">
        <div className="container-x grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
          <Reveal><Eyebrow>Resources & FAQ</Eyebrow><h2 className="mt-4 font-display text-3xl text-navy sm:text-4xl">Questions before you start?</h2><p className="mt-4 text-slate-600">Short answers to the questions we hear most often.</p></Reveal>
          <Reveal delay={100}><FaqList faqs={HOME_FAQS} id="home-faq" /></Reveal>
        </div>
      </section>
      <EnquirySection />
      <CtaBanner />
    </>
  );
}
